//src/pages/TenantSimpleTemplatesPage.tsx
import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCreateDepartmentTemplate,
  useCreateJobPositionTemplate,
  useDepartmentTemplates,
  useJobPositionTemplates,
  useSetDepartmentTemplateActive,
  useSetJobPositionTemplateActive,
  useUpdateDepartmentTemplate,
  useUpdateJobPositionTemplate,
} from "@/hooks/useModelImports";
import { PERMISSIONS } from "@/lib/permissions";
import type { TemplateItem } from "@/services/modelImports";

type TemplateKind = "departments" | "job_positions";

type Props = {
  kind: TemplateKind;
};

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}


function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function TenantSimpleTemplatesPage({ kind }: Props) {
  const isDepartment = kind === "departments";
  const title = isDepartment ? "Departamentos modelo" : "Cargos modelo";
  const description = isDepartment
    ? "Cadastre departamentos padrão da Empresa para importar nos Clientes."
    : "Cadastre cargos padrão da Empresa para importar nos Clientes.";
  const writePermission = isDepartment
    ? PERMISSIONS.TENANT_MODELS_DEPARTMENTS_WRITE
    : PERMISSIONS.TENANT_MODELS_JOB_POSITIONS_WRITE;

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<TemplateItem | null>(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<TemplateItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const departmentsQ = useDepartmentTemplates(true);
  const jobsQ = useJobPositionTemplates(true);
  const createDepartmentMut = useCreateDepartmentTemplate();
  const updateDepartmentMut = useUpdateDepartmentTemplate();
  const statusDepartmentMut = useSetDepartmentTemplateActive();
  const createJobMut = useCreateJobPositionTemplate();
  const updateJobMut = useUpdateJobPositionTemplate();
  const statusJobMut = useSetJobPositionTemplateActive();

  const query = isDepartment ? departmentsQ : jobsQ;
  const filtered = useMemo(() => {
    const items = query.data ?? [];
    const q = normalizeText(search);
    if (!q) return items;
    return items.filter((item) => normalizeText(item.name).includes(q));
  }, [query.data, search]);

  const busy = createDepartmentMut.isPending || updateDepartmentMut.isPending ||
    statusDepartmentMut.isPending || createJobMut.isPending || updateJobMut.isPending || statusJobMut.isPending;

  function resetForm() {
    setEditing(null);
    setName("");
  }

  async function handleSubmit() {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Informe o nome.");
      return;
    }

    try {
      if (isDepartment) {
        if (editing) await updateDepartmentMut.mutateAsync({ id: editing.id, name: cleanName });
        else await createDepartmentMut.mutateAsync(cleanName);
      } else {
        if (editing) await updateJobMut.mutateAsync({ id: editing.id, name: cleanName });
        else await createJobMut.mutateAsync(cleanName);
      }
      toast.success(editing ? "Modelo atualizado" : "Modelo criado");
      resetForm();
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao salvar modelo"));
    }
  }

  async function handleToggle() {
    if (!selected) return;
    try {
      const next = !selected.is_active;
      if (isDepartment) await statusDepartmentMut.mutateAsync({ id: selected.id, is_active: next });
      else await statusJobMut.mutateAsync({ id: selected.id, is_active: next });
      toast.success(next ? "Modelo ativado" : "Modelo desativado");
      setConfirmOpen(false);
      setSelected(null);
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Erro ao alterar status"));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />

      <Card className="p-4">
        <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <Label>{editing ? "Editar modelo" : "Novo modelo"}</Label>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={isDepartment ? "Ex.: Administrativo" : "Ex.: Técnico de Segurança do Trabalho"}
              className="mt-1"
            />
          </div>
          <div className="flex gap-2">
            {editing ? <Button variant="outline" onClick={resetForm} disabled={busy}>Cancelar</Button> : null}
            <PermissionButton permission={writePermission} onClick={handleSubmit} disabled={busy}>
              <Plus className="mr-2 h-4 w-4" />
              {editing ? "Salvar" : "Adicionar"}
            </PermissionButton>
          </div>
        </div>
      </Card>

      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar modelo"
          className="pl-8"
        />
      </div>

      {query.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando modelos...</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">Nenhum modelo encontrado.</Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[220px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
                      {item.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(item);
                        setName(item.name);
                      }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant={item.is_active ? "destructive" : "default"}
                      size="sm"
                      onClick={() => {
                        setSelected(item);
                        setConfirmOpen(true);
                      }}
                    >
                      {item.is_active ? "Desativar" : "Ativar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar modelo" : "Ativar modelo"}
        description={selected?.is_active ? "Deseja desativar este modelo?" : "Deseja ativar este modelo?"}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
        onConfirm={handleToggle}
      />
    </div>
  );
}
